/** Shared TanStack Query hooks for the integration config-editor widgets.
 *
 * Both lists are small and per-TO; several widget instances render inside the
 * same config grid, so we load each list ONCE (query key includes `toId`) and
 * let the query cache dedupe/share it. `staleTime` of 5 minutes avoids refetch
 * churn while the dialog is open. Passing query `''` returns the full list.
 */
import { useQuery } from '@tanstack/react-query';

import {
  searchImportSources,
  searchTagCategories,
  type ImportSource,
  type TagCategory,
} from '@/api/integrations';

const FIVE_MINUTES = 5 * 60 * 1000;

/** Full tag-category list for the TO (incl. franchise parent). Shared cache. */
export function useTagCategories(toId: number) {
  return useQuery<TagCategory[]>({
    queryKey: ['integrations', 'tagCategories', toId],
    queryFn: ({ signal }) => searchTagCategories(toId, '', signal),
    staleTime: FIVE_MINUTES,
    enabled: Number.isFinite(toId) && toId > 0,
  });
}

/** Cache key for the TO's import-source list.
 *
 *  Exported because anything that CREATES a key has to invalidate this, and a
 *  duplicated tuple is how that silently stops working: the list is cached with
 *  a five-minute staleTime, so without invalidation a newly minted key stays
 *  invisible and the picker cannot resolve the code it was just handed.
 */
export function importSourcesKey(toId: number) {
  return ['integrations', 'importSources', toId] as const;
}

/** Full import-source list for the TO. Shared cache. */
export function useImportSources(toId: number) {
  return useQuery<ImportSource[]>({
    queryKey: importSourcesKey(toId),
    queryFn: ({ signal }) => searchImportSources(toId, '', signal),
    staleTime: FIVE_MINUTES,
    enabled: Number.isFinite(toId) && toId > 0,
  });
}
